import Vue from 'vue'
import axios from "axios"
import {
    API_URL,
    SIGN_UP_USER_URL,
    GET_USER_URL
} from "./config.js"
import utilities from "../utilities"
import firebaseConfig from "./firebase.config.js"
import { successHandler, errorHandler } from "./status.validator.js"
import * as firebase from "firebase/app"
import "firebase/auth"
import "firebase/storage"
import "firebase/analytics"
import { CometChat } from "@cometchat-pro/chat"
import cometChatConfig from './comet.config'

const ApiService = {
    init() {
        axios.defaults.baseURL = API_URL
        // Intercepter
        this.setInterceptor()
        // Init firebase
        this.setFirebase()
        // Init analytics
        this.setFirebaseAnalytics()
        // Init Commet
        CommetService.initialize();

    },

    setInterceptor() {
        axios.interceptors.response.use(
            (response) => successHandler(response),
            (error) => errorHandler(error)
        )
    },

    setHeader(idToken) {
        if (idToken) {
            axios.defaults.headers.common[
                "firebase-auth"
            ] = `${idToken}`
        }
    },

    removeHeader() {
        delete axios.defaults.headers.common["firebase-auth"]
    },

    setFirebase() {
        firebase.initializeApp(firebaseConfig)
    },

    setFirebaseAnalytics() {
        firebase.analytics()
        Vue.prototype.$analytics = firebase.analytics();
    },

    get(resource, slug = "") {
        return axios.get(`${resource}/${slug}`)
    },

    post(resource, params) {
        return axios.post(`${resource}`, params)
    },

    delete(resource, slug = "") {
        return axios.delete(`${resource}/${slug}`)
    },

    patch(resource, params) {
        return axios.patch(`${resource}`, params)
    },


    firebaseCreateUser(params) {
        return firebase.auth().createUserWithEmailAndPassword(params.email, params.password)
            .then(({ user }) => {

                return user.getIdToken( /* forceRefresh */ true)
            })
            .then(idToken => {
                if (idToken) {
                    ApiService.setHeader(idToken)
                    return axios.post('/' + SIGN_UP_USER_URL, {
                        name: params.name,
                        email: params.email,
                        username: params.username
                    })
                }
            })
    },

    firebaseCreateUserUsingThirdParty(params) {
        let provider = null
        if(params.type === 'google') {
            provider = new firebase.auth.GoogleAuthProvider()
        } else if(params.type === 'facebook') {
            provider = new firebase.auth.FacebookAuthProvider()
        }
        
        let user = null
        return firebase.auth().signInWithPopup(provider)
            .then((res) => {
                user = res.user
                return firebase.auth().currentUser.getIdToken( /* forceRefresh */ true)
            })
            .then(idToken => {
                if (idToken) {
                    ApiService.setHeader(idToken)
                    return axios.post('/' + SIGN_UP_USER_URL, {
                        name: user.displayName,
                        email: user.email,
                        username: utilities.createUsername({
                            name: user.displayName
                        })
                    })
                }
            })
    },

    firebaseSigninUser(params) {
        return firebase.auth().signInWithEmailAndPassword(params.email, params.password)
            .then(({ user }) => {

                // CommetService.createUser({
                //     uid: this.user.firebase_id,
                //     name: this.user.name
                // })
                return user.getIdToken( /* forceRefresh */ true)
            })
            .then(idToken => {
                if (idToken) {
                    ApiService.setHeader(idToken)
                    return axios.get('/' + GET_USER_URL)
                }
            })
    },

    firebaseSendVerificationEmail() {
        const user = firebase.auth().currentUser;
        return user.sendEmailVerification()
    },

    firebaseCheckAuth() {
        return new Promise((resolve, reject) => {
                firebase.auth().onAuthStateChanged((user) => {
                    resolve(user)
                }, reject)
            })
            .then(user => {
                if (user) {

                    firebase.analytics().setUserId(user.uid)
                    firebase.analytics().setUserProperties({
                        account_type: "Basic"
                    })

                    return user.getIdToken( /* forceRefresh */ false)
                }
                throw new Error("Null User")
            })
            .then(idToken => {
                if (idToken) {
                    ApiService.setHeader(idToken)
                    //return idToken
                    return axios.get('/' + GET_USER_URL)
                }
            })
    },

    firebaseUpdateUserEmail(newEmail) {
        const user = firebase.auth().currentUser

        return user.updateEmail(newEmail)

    },

    firebaseResetPassword(params) {
        const email = params.email
        const auth = firebase.auth()

        return auth.sendPasswordResetEmail(email)
    },

    firebaseUpdateUserProfile(params) {
        const user = firebase.auth().currentUser

        return user.updateProfile({
            photoURL: params.photoURL
        })
    },

    firebaseReauthenticate(params) {
        const user = firebase.auth().currentUser
        const credentials = firebase.auth.EmailAuthProvider.credential(params.email, params.password)

        return user.reauthenticateWithCredential(credentials)
    },

    firebaseSignout() {
        return firebase.auth().signOut()
    },

    checkEmail(email) {
        return axios.get(`/${SIGN_UP_USER_URL}/email/${email}/validate`)
    },

    checkUsername(username) {
        return axios.get(`/${SIGN_UP_USER_URL}/username/${username}/validate`)
    }
}

export const FileService = {

    uploadFile(params, onUpload) {
        const storageRef = firebase.storage().ref()
        const uploadTask = storageRef.child('/post_images/' + params.file_name).put(params.file)
        const uploadTaskThumbnail = storageRef.child('/post_images/' + params.thumbnail.name).put(params.thumbnail)

        uploadTask.on('state_changed', onUpload)

        return Promise.all([uploadTask, uploadTaskThumbnail])
            .then(snapshots => {
                return Promise.all([snapshots[0].ref.getDownloadURL(), snapshots[1].ref.getDownloadURL()])
            })

    },

    deleteFile(params) {
        const storageRef = firebase.storage().ref()
        const deleteFile = storageRef.child('/post_images/' + params.file_name || params.file.name).delete()
        const deleteFileThumb = storageRef.child(`/post_images/${params.thumbnail ? params.thumbnail.name : params.thumbnail_file_name}`).delete()

        return Promise.all([deleteFile, deleteFileThumb])
    },

    uploadProfilePic(file) {
        const storageRef = firebase.storage().ref()
        const uploadTask = storageRef.child('/profile_pics/' + file.name).put(file)

        return uploadTask
            .then(snapshot => {
                return snapshot.ref.getDownloadURL()
            })
        // .then(url => {
        //     return ApiService.firebaseUpdateUserProfile({
        //         photoURL: url
        //     })
        // })
    },

    deletProfilePic(file) {
        const storageRef = firebase.storage().ref()
        const deleteFile = storageRef.child('/profile_pics/' + file.name).delete()

        return deleteFile
    }
}


export const CommetService = {

    initialize() {
        var appID = cometChatConfig.appID;
        var region = "us";

        var appSetting = new CometChat.AppSettingsBuilder().subscribePresenceForAllUsers().setRegion(region).build();

        return CometChat.init(appID, appSetting)
    },

    createUser(user) {
        let cometUser = new CometChat.User(user.uid);

        cometUser.setName(user.name);

        return CometChat.createUser(cometUser, cometChatConfig.authOnlyApiKey)
    },

    createGroup(groupData) {
        return new Promise((resolve, reject) => {
            var GUID = groupData.id;
            var groupName = groupData.name;
            var groupType = CometChat.GROUP_TYPE.PUBLIC;
            var password = "";

            var group = new CometChat.Group(GUID, groupName, groupType, password);

            CometChat.createGroup(group).then(
                group => {
                    resolve(group);
                },
                error => {
                    reject(error)
                }
            );
        });
    },

    joinGroup(groupData) {
        return new Promise((resolve, reject) => {
            var GUID = groupData.id;
            var password = "";
            var groupType = CometChat.GROUP_TYPE.PUBLIC;

            CometChat.joinGroup(GUID, groupType, password).then(
                group => {
                    resolve(group);
                },
                error => {
                    reject(error)
                }
            );
        });
    },

    loginUser(user) {
        return new Promise((resolve, reject) => {
            CometChat.login(user.uid.toLowerCase(), cometChatConfig.authOnlyApiKey).then(() => {
                resolve('commet-loged-in')
            }, (error) => {
                setTimeout(() => this.loginUser(user), 500);
                reject(error)
            });
        });
    },

    logOut() {
        return CometChat.logout()
    },

    sendMessage(payload) {
        return new Promise((resolve, reject) => {

            let receiverType = CometChat.RECEIVER_TYPE.USER;

            if (payload.receiverType == "group") {
                receiverType = CometChat.RECEIVER_TYPE.GROUP;
            }

            if (!payload.internal) {
                payload.messageText = cometChatConfig.textMessageKey + payload.messageText;
            } else {
                payload.messageText = cometChatConfig.internalMessageKey + payload.messageText;
            }

            let textMessage = new CometChat.TextMessage(
                payload.receiverID,
                payload.messageText,
                receiverType,
            );

            if (payload.metaData)
                textMessage.setMetadata(payload.metaData);

            CometChat.sendMessage(textMessage).then(
                message => {
                    resolve(message);
                },
                error => {
                    reject(error)
                }
            );
        });
    },

    formatMessageText(text) {
        return text.replace(cometChatConfig.textMessageKey, "");
    },

    isTextMessage(text) {
        return text.indexOf(cometChatConfig.textMessageKey) >= 0;
    },

    initializeFetchBuilder(receiverID, receiverType) {
        let messagesRequest

        if (receiverType == "group") {
            messagesRequest = new CometChat.MessagesRequestBuilder()
                .setLimit(cometChatConfig.messageHistoryLimit)
                .setCategory("message")
                .setSearchKeyword(cometChatConfig.textMessageKey)
                .setGUID(receiverID).build();
        } else {
            messagesRequest = new CometChat.MessagesRequestBuilder()
                .setLimit(cometChatConfig.messageHistoryLimit)
                .setCategory("message")
                .setSearchKeyword(cometChatConfig.textMessageKey)
                .setUID(receiverID).build();
        }

        return messagesRequest;
    },

}

export default ApiService